/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useRef } from 'preact/hooks';
import { isEqual } from '../../utils/UNSAFE_visUtils/eventsUtils';
import { useClearSelection } from './useClearSelection';

export type SelectionMode = 'none' | 'single' | 'multiple';
export type SelectionDetail<K, D> = {
  ids: K[];
  data: (D | undefined)[];
};
type SelectionBehavior = 'toggle' | 'replace';

const NAVKEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/**
 * The constant that defines time to wait to change selection after pointer up is fired.
 * If double click didn't happen
 */
const DBLCLICK_THRESHOLD = 250;

/**
 * Handles the selection.
 * @param itemId The id of the item.
 * @param selectedIds The set of selected item ids.
 * @param selectionMode The selection mode.
 * @param selectionBehavior The selection behavior. If 'toggle', unselected item will be selected and
 * vice versa. If 'replace', the item will replace the previous selection.
 * @param getDataById The callback to get item data from idDataMap.
 * @param onChange The callback to trigger when selection changes.
 */
const handleSelect = <K, D>(
  itemId: K | undefined,
  selectedIds: Set<K>,
  selectionMode: SelectionMode,
  selectionBehavior: SelectionBehavior,
  getDataById: (id: K) => D | undefined,
  onChange?: (detail: SelectionDetail<K, D>) => void
) => {
  let ids = new Set<K>([]);
  if (itemId != null) {
    ids = new Set(selectedIds);
    const isSelected = selectedIds.has(itemId);

    if (selectionBehavior === 'toggle') {
      if (isSelected) {
        ids.delete(itemId);
      } else if (selectionMode === 'single') {
        ids = new Set([itemId]); // only selection
      } else {
        ids.add(itemId); // add to selection in "multiple" selectionMode
      }
    } else if (!isSelected) {
      // In "multiple" selection mode, only replace selection if unselected item is selected.
      // In "single" selection mode, only need to replace if unselected item is selected.
      ids = new Set([itemId]);
    }
  }

  if (!isEqual<K>(ids, selectedIds)) {
    onChange?.({ ids: [...ids], data: [...ids].map(getDataById) });
  }
};

type Props<K, D> = {
  selection?: K[];
  selectionMode?: SelectionMode;
  idExtracter: (event: Event) => K | undefined;
  onChange?: (detail: SelectionDetail<K, D>) => void;
  isKeyboardSelection?: (event: KeyboardEvent) => boolean;
  isDrillEnabled?: boolean;
  getDataById: (id: K) => D | undefined;
};

/**
 * The hook to handle the selection behavior in vizualization components.
 */
export function useSelection<K, D>({
  selection = [],
  selectionMode = 'none',
  idExtracter,
  onChange,
  isDrillEnabled,
  isKeyboardSelection,
  getDataById
}: Props<K, D>) {
  const setTimeOutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearSelection = () => {
    handleSelect<K, D>(
      undefined,
      new Set(selection),
      selectionMode,
      'replace',
      getDataById,
      onChange
    );
  };

  const { clearSelectionContent, tryToggleClearButton } = useClearSelection<K>({ clearSelection });

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      const selectedIds = new Set(selection);
      if (setTimeOutRef.current && isDrillEnabled) {
        clearTimeout(setTimeOutRef.current);
        setTimeOutRef.current = undefined;
        // doubleClick happened
        return;
      }

      const key = idExtracter(event);

      if (tryToggleClearButton(event, key, selectionMode, selectedIds)) {
        return;
      }

      const isMeta = event.metaKey;
      const isCtrl = event.ctrlKey;
      // If there was a previous pointerUp event that occurred that should prevent selection,
      // event.preventDefault was called
      if (event.defaultPrevented || (key == null && (isMeta || isCtrl))) {
        return;
      }

      // TODO: Encorporate more logic of dvt.EventManager.prototype._onClick
      // this is a simple version of EventManager.prototype._onClick. Currently we are not checking
      // for event.pageX, event.pageY to determine if they are exactly clicked on the same point.
      // for now, the assumption is that if the pointerups are within 250ms, they are for a same double click.
      if (isDrillEnabled) {
        setTimeOutRef.current = setTimeout(() => {
          // double pointer up didn't happen
          handleSelect<K, D>(
            key,
            selectedIds,
            selectionMode,
            isMeta || isCtrl || event.pointerType === 'touch' ? 'toggle' : 'replace',
            getDataById,
            onChange
          );
          setTimeOutRef.current = undefined;
        }, DBLCLICK_THRESHOLD);
      } else {
        handleSelect<K, D>(
          key,
          selectedIds,
          selectionMode,
          isMeta || isCtrl || event.pointerType === 'touch' ? 'toggle' : 'replace',
          getDataById,
          onChange
        );
      }
    },
    [
      selection,
      selectionMode,
      onChange,
      idExtracter,
      isDrillEnabled,
      getDataById,
      tryToggleClearButton
    ]
  );

  const onContextMenu = useCallback(
    (event: MouseEvent) => {
      const key = idExtracter(event);
      const isCtrl = event.ctrlKey;
      handleSelect<K, D>(
        key,
        new Set(selection),
        selectionMode,
        isCtrl ? 'toggle' : 'replace',
        getDataById,
        onChange
      );
    },
    [selection, selectionMode, onChange, idExtracter, getDataById]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      const isShift = event.shiftKey;
      const isSpaceCtrl = event.ctrlKey && event.key === ' ';
      const isArrowKey = NAVKEYS.indexOf(event.key) !== -1;
      const itemKey = idExtracter(event);

      if (isArrowKey || isSpaceCtrl || (isKeyboardSelection && isKeyboardSelection(event))) {
        const behavior = isShift || isSpaceCtrl ? 'toggle' : 'replace';
        handleSelect<K, D>(
          itemKey,
          new Set(selection),
          selectionMode,
          behavior,
          getDataById,
          onChange
        );
      }
    },
    [selection, selectionMode, onChange, idExtracter, isKeyboardSelection, getDataById]
  );

  return {
    selectionContent: clearSelectionContent,
    selectionProps:
      selectionMode === 'none' || onChange == null
        ? {}
        : {
            onKeyUp,
            onContextMenu,
            onPointerUp
          }
  };
}
